/**
 * Pipeline Health — where every item stands, counted from what is stored.
 *
 * ── The model this follows ─────────────────────────────────────────────────
 * The stored item state is `exit_status` (active | deferred | archived), and
 * readiness is computed, never stored (canon 01, "Exit Status" / "Readiness").
 * The Decision Unit register (docs/3-system/00-concept-registers/item-and-work.yaml)
 * names the ladder an active item climbs: needs definition → ready to
 * prioritize → pending evaluation → evaluated → committed. Triage sits in
 * front of it. This module places each item on that ladder at the furthest
 * rung it has reached.
 *
 * ── Why the predicates are imported, not re-stated ─────────────────────────
 * "Awaiting triage" and "needs definition" are the same functions the Triage
 * Queue, the Needs Definition page and the sidebar badges use
 * (lib/intakeQueueVisibility.ts). The dashboard, the list and the badges
 * therefore cannot disagree about a count — the number on the funnel is the
 * number on the page the funnel opens.
 *
 * React-free so it is unit-testable. Stores are read by
 * hooks/prioritization/usePipelineInputs.ts.
 */

import {
  itemExitStatus,
  itemIsAwaitingTriage,
  itemNeedsDefinition,
  triageFinalizedOutOfQueueIds,
} from './intakeQueueVisibility';
import type { TriageAssessment } from '../types/prioritization/triage';

// ============================================================================
// Stages
// ============================================================================

/** The rungs an active item climbs, in order. */
export type PipelineStage =
  | 'awaiting_triage'
  | 'needs_definition'
  | 'ready'
  | 'pending_result'
  | 'ranked'
  | 'committed';

/** A stage, or one of the two exits an item can take instead. */
export type PipelineBucket = PipelineStage | 'deferred' | 'archived';

export interface PipelineStageDef {
  key: PipelineStage;
  label: string;
  /** One sentence, plain words — shown as the stage's tooltip and in the CSV. */
  description: string;
}

export const PIPELINE_STAGES: ReadonlyArray<PipelineStageDef> = [
  { key: 'awaiting_triage', label: 'Awaiting triage', description: 'Submitted and not yet looked at. The same items as the Triage Queue.' },
  { key: 'needs_definition', label: 'Needs definition', description: 'Missing a category, an altitude, or the scoping answers. The same items as the Needs Definition page.' },
  { key: 'ready', label: 'Ready to prioritize', description: 'Defined and carrying every required input, but not yet in a pool or a session.' },
  { key: 'pending_result', label: 'In a pool or session', description: 'Curated into an item pool or placed in a session that has not produced a result for it.' },
  { key: 'ranked', label: 'Ranked', description: 'Appears in at least one session result.' },
  { key: 'committed', label: 'Committed', description: 'Part of a priority list that has been decided, ratified, or published.' },
];

export const PIPELINE_EXITS: ReadonlyArray<{ key: 'deferred' | 'archived'; label: string; description: string }> = [
  { key: 'deferred', label: 'Deferred', description: 'Explicitly postponed. Comes back when its review date arrives or someone restores it.' },
  { key: 'archived', label: 'Archived', description: 'Removed from consideration. Restorable.' },
];

export const PIPELINE_BUCKET_LABEL: Record<PipelineBucket, string> = {
  awaiting_triage: 'Awaiting triage',
  needs_definition: 'Needs definition',
  ready: 'Ready to prioritize',
  pending_result: 'In a pool or session',
  ranked: 'Ranked',
  committed: 'Committed',
  deferred: 'Deferred',
  archived: 'Archived',
};

export const PIPELINE_BUCKETS: ReadonlyArray<PipelineBucket> = [
  ...PIPELINE_STAGES.map((s) => s.key),
  'deferred',
  'archived',
];

export function isPipelineBucket(value: unknown): value is PipelineBucket {
  return typeof value === 'string' && (PIPELINE_BUCKETS as ReadonlyArray<string>).includes(value);
}

// ============================================================================
// Classification
// ============================================================================

/** The slice of an item the classifier reads. Every field is optional on purpose:
    rows arrive from the store in camelCase and from Supabase in snake_case. */
export interface PipelineItem {
  id: string;
  lifecycle_stage?: string;
  lifecycleStage?: string;
  exit_status?: string;
  exitStatus?: string;
  itemCategory?: string | null;
  altitudeLevel?: number | null;
  altitudeScopingInputs?: unknown;
  altitude_scoping_inputs?: unknown;
}

/** Everything the classifier needs that does not live on the item itself. */
export interface PipelineInputs {
  /** Items with a finalized triage assessment (left the queue). */
  triagedOutIds: ReadonlySet<string>;
  /** Items in an item pool or in a session's item set. */
  pendingResultIds: ReadonlySet<string>;
  /** Items that appear in a session result. */
  rankedIds: ReadonlySet<string>;
  /** Items in a priority list that is decided, ratified, or published. */
  committedIds: ReadonlySet<string>;
  /** Computed readiness — injected so this module stays store-free. Called only
      for items that are defined, so the cost is bounded by the rung, not the list. */
  isReady: (item: PipelineItem) => boolean;
}

/** Priority-set states that count as a commitment (canon 08: decided and beyond). */
export const COMMITTED_PRIORITY_SET_STATES: ReadonlySet<string> = new Set(['decided', 'ratified', 'published']);

export interface BuildPipelineInputsArgs {
  assessments: Record<string, TriageAssessment>;
  sessions: ReadonlyArray<{
    itemIds?: string[] | null;
    rankingOutput?: { rankedItemIds?: string[] | null } | null;
  }>;
  itemPools: ReadonlyArray<{ priorityIds?: string[] | null }>;
  prioritySets: ReadonlyArray<{ state: string; itemIds?: string[] | null; rankedItemIds?: string[] | null }>;
  isReady: (item: PipelineItem) => boolean;
}

export function buildPipelineInputs(args: BuildPipelineInputsArgs): PipelineInputs {
  const pendingResultIds = new Set<string>();
  const rankedIds = new Set<string>();
  const committedIds = new Set<string>();

  for (const pool of args.itemPools) {
    for (const id of pool.priorityIds ?? []) pendingResultIds.add(id);
  }
  for (const session of args.sessions) {
    for (const id of session.itemIds ?? []) pendingResultIds.add(id);
    for (const id of session.rankingOutput?.rankedItemIds ?? []) rankedIds.add(id);
  }
  for (const set of args.prioritySets) {
    if (!COMMITTED_PRIORITY_SET_STATES.has(set.state)) continue;
    for (const id of set.itemIds ?? []) committedIds.add(id);
    for (const id of set.rankedItemIds ?? []) committedIds.add(id);
  }

  return {
    triagedOutIds: triageFinalizedOutOfQueueIds(args.assessments),
    pendingResultIds,
    rankedIds,
    committedIds,
    isReady: args.isReady,
  };
}

/**
 * Place one item. Exits first (a deferred item is deferred whatever it did
 * before), then the furthest rung reached, then the front of the ladder.
 */
export function classifyPipelineBucket(item: PipelineItem, inputs: PipelineInputs): PipelineBucket {
  const exit = itemExitStatus(item);
  if (exit === 'archived') return 'archived';
  if (exit === 'deferred') return 'deferred';

  if (inputs.committedIds.has(item.id)) return 'committed';
  if (inputs.rankedIds.has(item.id)) return 'ranked';
  if (inputs.pendingResultIds.has(item.id)) return 'pending_result';

  if (itemIsAwaitingTriage(item, inputs.triagedOutIds)) return 'awaiting_triage';
  if (itemNeedsDefinition(item)) return 'needs_definition';
  if (inputs.isReady(item)) return 'ready';
  // Defined but a required input is still missing: that is definition work too.
  return 'needs_definition';
}

export function countByBucket<T extends PipelineItem>(
  items: ReadonlyArray<T>,
  inputs: PipelineInputs,
): Record<PipelineBucket, number> {
  const counts = Object.fromEntries(PIPELINE_BUCKETS.map((b) => [b, 0])) as Record<PipelineBucket, number>;
  for (const item of items) counts[classifyPipelineBucket(item, inputs)] += 1;
  return counts;
}

// ============================================================================
// Category mix
// ============================================================================

/** Canonical category order — fixed, never cycled (lib/itemCategoryColors.ts). */
export const CATEGORY_ORDER: ReadonlyArray<string> = [
  'problem', 'opportunity', 'risk', 'solution', 'commitment', 'investment',
];

export const UNSET_CATEGORY = 'unset';

export function categoryOf(item: { itemCategory?: string | null }): string {
  const c = item.itemCategory?.trim().toLowerCase();
  return c ? c : UNSET_CATEGORY;
}

export interface CountedSlice {
  key: string;
  count: number;
}

/** Counts by category in canonical order; unknown categories follow, then "unset". */
export function categoryMix(items: ReadonlyArray<{ itemCategory?: string | null }>): CountedSlice[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = categoryOf(item);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const ordered: CountedSlice[] = [];
  for (const key of CATEGORY_ORDER) {
    if (counts.has(key)) ordered.push({ key, count: counts.get(key)! });
  }
  const extras = [...counts.keys()]
    .filter((k) => !CATEGORY_ORDER.includes(k) && k !== UNSET_CATEGORY)
    .sort();
  for (const key of extras) ordered.push({ key, count: counts.get(key)! });
  if (counts.has(UNSET_CATEGORY)) ordered.push({ key: UNSET_CATEGORY, count: counts.get(UNSET_CATEGORY)! });
  return ordered;
}

// ============================================================================
// Breakdown (by queue / org unit)
// ============================================================================

export interface BreakdownRow {
  /** Group id ('' when the item has no group). */
  group: string;
  counts: Record<string, number>;
  total: number;
}

/** Rows per group, largest first; within a row the counts are keyed by `valueOf`. */
export function breakdown<T>(
  items: ReadonlyArray<T>,
  groupOf: (item: T) => string | null | undefined,
  valueOf: (item: T) => string,
): BreakdownRow[] {
  const rows = new Map<string, BreakdownRow>();
  for (const item of items) {
    const group = groupOf(item) ?? '';
    let row = rows.get(group);
    if (!row) {
      row = { group, counts: {}, total: 0 };
      rows.set(group, row);
    }
    const value = valueOf(item);
    row.counts[value] = (row.counts[value] ?? 0) + 1;
    row.total += 1;
  }
  return [...rows.values()].sort((a, b) => b.total - a.total || a.group.localeCompare(b.group));
}

// ============================================================================
// Age
// ============================================================================

const DAY_MS = 24 * 60 * 60 * 1000;

export function daysSince(date: Date | string | number | null | undefined, now: number): number | null {
  if (date == null) return null;
  const ms = date instanceof Date ? date.getTime() : new Date(date).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.floor((now - ms) / DAY_MS));
}

/** The thresholds the dashboard offers. Each lands on a bucket edge below, so
    "past the threshold" is always a whole bucket, never a split one. */
export const STALE_THRESHOLDS: ReadonlyArray<number> = [14, 30, 60, 90];
export const DEFAULT_STALE_THRESHOLD = 30;

export type AgeSeverity = 'none' | 'warning' | 'serious';

export interface AgeBucket {
  key: string;
  label: string;
  minDays: number;
  /** null = open-ended. */
  maxDays: number | null;
  count: number;
  /** warning: past the threshold. serious: past twice the threshold. */
  severity: AgeSeverity;
}

const AGE_EDGES: ReadonlyArray<{ key: string; label: string; min: number; max: number | null }> = [
  { key: '0-7', label: '0–7 days', min: 0, max: 7 },
  { key: '8-14', label: '8–14', min: 8, max: 14 },
  { key: '15-30', label: '15–30', min: 15, max: 30 },
  { key: '31-60', label: '31–60', min: 31, max: 60 },
  { key: '61-90', label: '61–90', min: 61, max: 90 },
  { key: '91+', label: '91+', min: 91, max: null },
];

export function ageSeverity(days: number, thresholdDays: number): AgeSeverity {
  if (days > thresholdDays * 2) return 'serious';
  if (days > thresholdDays) return 'warning';
  return 'none';
}

export interface AgeDistribution {
  buckets: AgeBucket[];
  /** Items past the threshold (warning + serious). */
  pastThreshold: number;
  /** Items past twice the threshold. */
  serious: number;
  /** Items whose timestamp could not be read. */
  unknown: number;
}

/**
 * Histogram of how long items have sat since they last changed.
 *
 * "Age in current state" has no stored timestamp for active items — the state
 * is `exit_status`, and the entry into `active` is creation. What is stored is
 * `updated_at`, and that is the same signal the item list's Stale filter
 * reads (useItemListData: 30 days since last update). Using it here keeps
 * "stale" meaning one thing across the product.
 */
export function ageDistribution(
  items: ReadonlyArray<{ updatedAt?: Date | string | null; createdAt?: Date | string | null }>,
  now: number,
  thresholdDays: number,
): AgeDistribution {
  const buckets: AgeBucket[] = AGE_EDGES.map((e) => ({
    key: e.key,
    label: e.label,
    minDays: e.min,
    maxDays: e.max,
    count: 0,
    severity: ageSeverity(e.min, thresholdDays),
  }));
  let pastThreshold = 0;
  let serious = 0;
  let unknown = 0;

  for (const item of items) {
    const days = daysSince(item.updatedAt ?? item.createdAt, now);
    if (days === null) {
      unknown += 1;
      continue;
    }
    const bucket = buckets.find((b) => days >= b.minDays && (b.maxDays === null || days <= b.maxDays));
    if (bucket) bucket.count += 1;
    const sev = ageSeverity(days, thresholdDays);
    if (sev !== 'none') pastThreshold += 1;
    if (sev === 'serious') serious += 1;
  }

  return { buckets, pastThreshold, serious, unknown };
}

// ============================================================================
// Readiness gaps (heatmap)
// ============================================================================

export interface ReadinessGapCell {
  id: string;
  title: string;
  /** Number of required inputs still missing. 0 = ready. */
  gaps: number;
  /** The missing inputs, in plain words. */
  missing: string[];
}

/**
 * Cells for the heatmap: most gaps first, then alphabetical so the order is
 * stable between renders. `limit` keeps the grid readable; the caller shows
 * "N of M" when it truncates.
 */
export function readinessGapCells<T extends { id: string; title?: string | null }>(
  items: ReadonlyArray<T>,
  gapsOf: (item: T) => string[],
  limit: number,
): { cells: ReadinessGapCell[]; total: number; maxGaps: number } {
  const all: ReadinessGapCell[] = items.map((item) => {
    const missing = gapsOf(item);
    return { id: item.id, title: item.title?.trim() || 'Untitled', gaps: missing.length, missing };
  });
  all.sort((a, b) => b.gaps - a.gaps || a.title.localeCompare(b.title));
  const maxGaps = all.reduce((m, c) => Math.max(m, c.gaps), 0);
  return { cells: all.slice(0, limit), total: all.length, maxGaps };
}
